import { useState, useEffect, useRef } from "react";
import { Lobby } from "./components/Lobby";
import { Room } from "./components/Room";
import type { Message, Phase } from "./types";
import "./index.css";

import logo from "./logo.svg";

export function App() {
  const [nickname, setNickname] = useState<string | null>(() => localStorage.getItem("eurovizion_nickname"));
  const [roomCode, setRoomCode] = useState<string | null>(() => localStorage.getItem("eurovizion_roomCode"));
  const [users, setUsers] = useState<string[]>([]);
  const [picks, setPicks] = useState<Record<string, number[]>>({});
  const [phase, setPhase] = useState<Phase>("drafting");
  const [lockedUsers, setLockedUsers] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);

  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  useEffect(() => {
    if (nickname && roomCode) {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = ws;

      let reconnectTimeout: Timer;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        const joinMessage: Message = {
          type: "JOIN_ROOM",
          payload: { nickname, roomCode },
        };
        ws.send(JSON.stringify(joinMessage));
      };

      ws.onmessage = (event) => {
        const data: Message = JSON.parse(event.data);
        if (data.type === "SYNC_STATE") {
          setUsers(data.payload.users);
          setPicks(data.payload.picks);
          setPhase(data.payload.phase);
          setLockedUsers(data.payload.lockedUsers);
        } else if (data.type === "USER_JOINED") {
          setUsers(data.payload.users);
        } else if (data.type === "DRAFT_UPDATED") {
          setPicks(prev => ({ ...prev, ...data.payload.picks }));
        } else if (data.type === "PHASE_UPDATED") {
          setPhase(data.payload.phase);
        } else if (data.type === "USER_LOCKED") {
          setLockedUsers(prev => [...prev, data.payload.nickname]);
        }
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
        // Attempt to reconnect after 3 seconds
        reconnectTimeout = setTimeout(() => {
          setReconnectAttempt(prev => prev + 1);
        }, 3000);
      };

      return () => {
        ws.onclose = null; // Important: prevent onclose loop during cleanup
        ws.close();
        clearTimeout(reconnectTimeout);
      };
    }
  }, [nickname, roomCode, reconnectAttempt]);

  const handleJoin = (name: string, code: string) => {
    localStorage.setItem("eurovizion_nickname", name);
    localStorage.setItem("eurovizion_roomCode", code);
    setNickname(name);
    setRoomCode(code);
  };

  const handleTogglePick = (songId: number) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "TOGGLE_PICK",
        payload: { songId }
      }));
    }
  };

  const handleStartLocking = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "START_LOCKING_PHASE"
      }));
    }
  };

  const handleLockFinal = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "LOCK_FINAL"
      }));
    }
  };

  const handleRevealAll = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "REVEAL_ALL"
      }));
    }
  };

  const handleResetRoom = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "RESET_ROOM"
      }));
    }
  };

  const handleLeaveRoom = () => {
    localStorage.removeItem("eurovizion_nickname");
    localStorage.removeItem("eurovizion_roomCode");
    setNickname(null);
    setRoomCode(null);
    setUsers([]);
    setPicks({});
    setLockedUsers([]);
    setPhase("drafting");
    setIsConnected(false);
  };

  return (
    <div className="container mx-auto p-8 relative z-10 max-w-4xl">
      <div className="flex justify-center items-center gap-4 mb-8">
        <img
          src={logo}
          alt="Eurovizion Logo"
          className="h-16 transition-all duration-300 hover:drop-shadow-[0_0_2em_#ff008caa]"
        />
        <h1 className="text-4xl font-black tracking-tighter uppercase italic euro-text-gradient">
          Eurovizion
        </h1>
      </div>
      
      {!nickname || !roomCode ? (
        <Lobby onJoin={handleJoin} />
      ) : (
        <Room 
          roomCode={roomCode} 
          users={users} 
          picks={picks} 
          currentUser={nickname}
          phase={phase}
          lockedUsers={lockedUsers}
          isConnected={isConnected}
          onTogglePick={handleTogglePick}
          onStartLocking={handleStartLocking}
          onLockFinal={handleLockFinal}
          onRevealAll={handleRevealAll}
          onResetRoom={handleResetRoom}
          onLeaveRoom={handleLeaveRoom}
        />
      )}
    </div>
  );
}

export default App;
